import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ConditionClause, ConfigurationData } from '../../types';
import ConditionTableRow from './ConditionTableRow';
import ExpressionPreview from './ExpressionPreview';

interface TableConditionEditorProps {
  conditions: ConditionClause[];
  onUpdateCondition: (clauseId: string, updates: Partial<ConditionClause>) => void;
  onApplyCondition: (clauseId: string) => void;
  onRemoveCondition: (clauseId: string) => void;
  onAddCondition: () => void;
  onClose?: () => void; // ESC key handler
  onValidateExpression?: () => void;
  config?: ConfigurationData | null;
  showExpressionPreview?: boolean;
  onToggleExpressionPreview?: () => void;
  unappliedChanges?: Record<string, boolean>; // Track which conditions have unapplied changes
}

const TableConditionEditor: React.FC<TableConditionEditorProps> = ({
  conditions,
  onUpdateCondition,
  onApplyCondition,
  onRemoveCondition,
  onAddCondition,
  onClose,
  onValidateExpression,
  config,
  showExpressionPreview = false,
  onToggleExpressionPreview,
  unappliedChanges = {},
}) => {
  // Handle ESC key press to close dialog
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Memoized condition rows to prevent unnecessary re-renders
  const conditionRows = useMemo(() => {
    return conditions.map((condition, index) => (
      <ConditionTableRow
        key={condition.id}
        condition={condition}
        _isFirst={index === 0}
        isLast={index === conditions.length - 1}
        onUpdate={(updates: Partial<ConditionClause>) => onUpdateCondition(condition.id, updates)}
        onApply={() => onApplyCondition(condition.id)}
        onRemove={() => onRemoveCondition(condition.id)}
        config={config}
        hasUnappliedChanges={unappliedChanges[condition.id] || false}
      />
    ));
  }, [
    conditions,
    onUpdateCondition,
    onApplyCondition,
    onRemoveCondition,
    config,
    unappliedChanges,
  ]);

  const handleTogglePreview = useCallback(() => {
    if (onToggleExpressionPreview) {
      onToggleExpressionPreview();
    }
  }, [onToggleExpressionPreview]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Expression Preview */}
      {onToggleExpressionPreview && (
        <ExpressionPreview
          conditions={conditions}
          visible={showExpressionPreview}
          onToggle={handleTogglePreview}
          onValidate={onValidateExpression}
          config={config}
        />
      )}

      {/* Conditions Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Table stickyHeader sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Element</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Operator</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Values</TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: 120 }}>
                Actions
              </TableCell>
              <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: 80 }}>
                Logic
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conditionRows}

            {/* Add Condition Row */}
            <TableRow>
              <TableCell colSpan={5} sx={{ py: 2 }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={onAddCondition}
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    borderStyle: 'dashed',
                    '&:hover': {
                      borderStyle: 'solid',
                    },
                  }}
                >
                  Add Condition
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Empty state */}
        {conditions.length === 0 && (
          <Box
            sx={{
              p: 6,
              textAlign: 'center',
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No conditions defined
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Keyboard Hints */}
      <Box sx={{ mt: 1, textAlign: 'right' }}>
        <Typography variant="caption" color="text.secondary">
          Press ESC to close • Tab to navigate between fields
        </Typography>
      </Box>
    </Box>
  );
};

export default TableConditionEditor;
